import { DateTime } from 'luxon'
import { getEffectiveTimezone } from './getEffectiveTimezone'
import { normalizeStoreTimeForInput } from './normalizeStoreTimeForInput'
import { Normalization } from './normalization'
import {
  DayOfWeekValues,
  LeadTimeType,
  LeadTimeTypeValues,
  MenuStatus,
  MenuStatusValues
} from '~/enums'
import type { OrderType } from '~/enums'
import { isOrderType } from '~/enums/order/isOrderType'
import type { TimeSlotStore } from './TimeSlotStore'

type OperatingHour = NonNullable<TimeSlotStore['operating_hours']>[number]

interface MenuScheduleItem {
  day_of_week?: unknown
  day?: unknown
  start_time?: unknown
  end_time?: unknown
  is_enabled?: unknown
  enabled?: unknown
  is_available?: unknown
  specific_date?: unknown
}

interface AvailableMenu {
  status?: unknown
  is_active?: unknown
  availability_mode?: unknown
  sync_with_store_hours?: unknown
  schedule?: MenuScheduleItem[]
  custom_hours?: MenuScheduleItem[]
  days?: unknown[]
  start_time?: unknown
  end_time?: unknown
}

interface TimeSlotOptions {
  eventDate?: string | null
  orderType?: OrderType | null
  store?: TimeSlotStore | null
  menus?: AvailableMenu[] | null
  now?: DateTime
}

const DAY_NAMES: readonly string[] = DayOfWeekValues

const toInputString = (value: unknown): string => Normalization.stringValue(value, '') ?? ''

const normalizeKey = (value: unknown) =>
  toInputString(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

const normalizeDay = (value: unknown) => {
  const normalized = normalizeKey(value)
  return /^\d+$/.test(normalized) ? (DAY_NAMES[Number(normalized)] ?? normalized) : normalized
}

const isEnabled = (value: unknown) => {
  if (value === undefined || value === null) return true
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  return !['0', 'false', 'no', 'off'].includes(normalizeKey(value))
}

const hasValue = (value: unknown): boolean => {
  return value !== null && value !== undefined && (typeof value !== 'string' || value.length > 0)
}

const storeTimeToMinutes = (value: unknown) => {
  const raw = toInputString(value).trim()
  const meridiem = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i.exec(raw)
  if (meridiem) {
    let hour = Number(meridiem[1])
    const minute = Number(meridiem[2] ?? 0)
    if (meridiem[3]?.toLowerCase() === 'pm' && hour < 12) hour += 12
    if (meridiem[3]?.toLowerCase() === 'am' && hour === 12) hour = 0
    return hour * 60 + minute
  }

  const normalized = normalizeStoreTimeForInput(raw)
  if (!normalized) return null

  const [hour, minute] = normalized.split(':').map(Number)
  if (
    hour === undefined ||
    minute === undefined ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  )
    return null
  return hour * 60 + minute
}

/** Equal open/close is open 24 hours; an overnight row covers its own day from open to midnight and its tail belongs to the previous day's row. */
const isWithinSameDayRange = (minutes: number, startValue: unknown, endValue: unknown) => {
  const start = storeTimeToMinutes(startValue)
  const end = storeTimeToMinutes(endValue)
  if (start === null || end === null) return false

  if (start === end) return true
  if (start < end) return minutes >= start && minutes < end
  return minutes >= start
}

/** The after-midnight portion of an overnight row (open >= close), attributed to the next day. */
const previousDayTailMinutes = (minutes: number, startValue: unknown, endValue: unknown) => {
  const start = storeTimeToMinutes(startValue)
  const end = storeTimeToMinutes(endValue)
  if (start === null || end === null || start < end) return false

  return minutes < end
}

const dayNameOf = (dateTime: DateTime): string => dateTime.toFormat('cccc').toLowerCase()

const previousDayOf = (day: string): string => {
  const index = DAY_NAMES.indexOf(day)
  return index === -1 ? day : (DAY_NAMES[(index + DAY_NAMES.length - 1) % DAY_NAMES.length] ?? day)
}

const getSchedule = (menu: AvailableMenu): MenuScheduleItem[] => {
  const schedule = menu.custom_hours ?? menu.schedule ?? []
  if (Array.isArray(schedule) && schedule.length > 0) return schedule

  if (
    Array.isArray(menu.days) &&
    menu.days.length > 0 &&
    hasValue(menu.start_time) &&
    hasValue(menu.end_time)
  ) {
    return menu.days.map((day) => ({
      day_of_week: day,
      start_time: menu.start_time,
      end_time: menu.end_time,
      is_enabled: true
    }))
  }

  return []
}

const isActiveMenu = (menu: AvailableMenu) => {
  if (menu.is_active === false) return false
  const status = Normalization.enumValue(menu.status, MenuStatusValues)
  return !status || status === MenuStatus.ACTIVE || status === MenuStatus.PUBLISHED
}

const menuUsesStoreHours = (menu: AvailableMenu) => {
  const mode = normalizeKey(menu.availability_mode)
  if (mode === 'custom') return false
  if (getSchedule(menu).length > 0) return false
  if (mode === 'sync' || menu.sync_with_store_hours === true) return true
  return true
}

const matchesScheduleDate = (entry: MenuScheduleItem, date: string, day: string) =>
  (!hasValue(entry.specific_date) || String(entry.specific_date).slice(0, 10) === date) &&
  (!hasValue(entry.day_of_week ?? entry.day) ||
    normalizeDay(entry.day_of_week ?? entry.day) === day)

const hasMenuAtTime = (
  menus: AvailableMenu[] | null | undefined,
  eventDateTime: DateTime<true>,
  minutes: number
) => {
  if (!Array.isArray(menus) || menus.length === 0) return true

  const activeMenus = menus.filter(isActiveMenu)
  if (activeMenus.length === 0) return false

  return activeMenus.some((menu) => {
    if (menuUsesStoreHours(menu)) return true

    const previous = eventDateTime.minus({ days: 1 })
    // An overnight entry's after-midnight tail belongs to the following date, as the server reports it.
    return getSchedule(menu).some(
      (entry) =>
        isEnabled(entry.is_enabled ?? entry.enabled ?? entry.is_available) &&
        ((matchesScheduleDate(entry, eventDateTime.toISODate(), dayNameOf(eventDateTime)) &&
          isWithinSameDayRange(minutes, entry.start_time, entry.end_time)) ||
          (matchesScheduleDate(entry, previous.toISODate(), dayNameOf(previous)) &&
            previousDayTailMinutes(minutes, entry.start_time, entry.end_time)))
    )
  })
}

const isWithinOperatingHours = (
  operatingHours: OperatingHour[] | null | undefined,
  orderType: OrderType | null | undefined,
  day: string,
  minutes: number
) => {
  if (!Array.isArray(operatingHours)) return true

  // One shared schedule covers every order type, so a valid type only gates whether a slot is offered.
  if (!isOrderType(orderType)) return false

  const matchesRow = (entry: OperatingHour, targetDay: string) =>
    normalizeDay(entry.day_of_week) === targetDay && isEnabled(entry.is_open)

  const sameDayOpen = operatingHours.some(
    (entry) =>
      matchesRow(entry, day) && isWithinSameDayRange(minutes, entry.open_time, entry.close_time)
  )
  if (sameDayOpen) return true

  // The previous day's overnight row still covers this day's early minutes.
  const previousDay = previousDayOf(day)
  return operatingHours.some(
    (entry) =>
      matchesRow(entry, previousDay) &&
      previousDayTailMinutes(minutes, entry.open_time, entry.close_time)
  )
}

const satisfiesLeadTime = (
  eventDateTime: DateTime,
  store: TimeSlotStore | null | undefined,
  now: DateTime
) => {
  if (eventDateTime <= now) return false

  const leadTimeType = Normalization.enumValue(store?.lead_time_type, LeadTimeTypeValues)
  if (leadTimeType === LeadTimeType.SAME_DAY) {
    const leadTimeHours = Math.max(0, Normalization.floatValue(store?.lead_time_hours) ?? 0)
    return eventDateTime >= now.plus({ hours: leadTimeHours })
  }

  if (leadTimeType === LeadTimeType.DAY_BEFORE) {
    const eventDay = eventDateTime.startOf('day')
    const tomorrow = now.startOf('day').plus({ days: 1 })
    if (eventDay < tomorrow) return false

    const cutoffTime = store?.day_before_cutoff_time
    if (eventDay.equals(tomorrow) && hasValue(cutoffTime)) {
      const cutoffMinutes = storeTimeToMinutes(cutoffTime)
      if (cutoffMinutes !== null && now.hour * 60 + now.minute > cutoffMinutes) return false
    }
  }

  if (leadTimeType === LeadTimeType.MULTIPLE_DAYS) {
    const noticeDays = Math.max(
      0,
      Math.ceil(Normalization.floatValue(store?.multiple_days_notice) ?? 0)
    )
    return eventDateTime.startOf('day') >= now.startOf('day').plus({ days: noticeDays })
  }

  return true
}

export const isOrderTimeSlotAvailable = (
  time: string,
  { eventDate, orderType, store, menus, now: providedNow }: TimeSlotOptions
) => {
  if (eventDate === null || eventDate === undefined || eventDate.length === 0) return true

  const timezone = getEffectiveTimezone(store?.timezone)
  const normalizedTime = normalizeStoreTimeForInput(time)
  if (!normalizedTime) return false

  const eventDateTime = DateTime.fromISO(`${eventDate}T${normalizedTime}`, { zone: timezone })
  if (!eventDateTime.isValid) return false

  const now = (providedNow ?? DateTime.now()).setZone(timezone)
  const day = dayNameOf(eventDateTime)
  const minutes = eventDateTime.hour * 60 + eventDateTime.minute

  return (
    satisfiesLeadTime(eventDateTime, store, now) &&
    isWithinOperatingHours(store?.operating_hours, orderType, day, minutes) &&
    hasMenuAtTime(menus, eventDateTime, minutes)
  )
}
